// Les 750 variantes gratuites (§11) : 150 classes × 5 styles de base. Chaque variante combine un matériau
// propre au style (pas un simple recoloriage), un détail propre à la famille et des galons selon le rang
// de la classe. Rendu procédural déterministe (client/src/game/render/variants.ts) ; la hitbox, la silhouette
// fonctionnelle et la lisibilité des tubes ne changent jamais.
import { CLASSES, type ClassNode } from './classes';

export const STYLES = ['atelier', 'desert', 'boreal', 'obsidienne', 'signal'] as const;
export type StyleId = (typeof STYLES)[number];

/** Code transmis dans le snapshot (0 = défaut Atelier, 10+ = cosmétiques de la boutique). */
export const STYLE_CODE: Record<StyleId, number> = { atelier: 1, desert: 2, boreal: 3, obsidienne: 4, signal: 5 };
export const STYLE_BY_CODE: (StyleId | null)[] = [null, 'atelier', 'desert', 'boreal', 'obsidienne', 'signal'];

export interface StyleDef {
  name: string;
  material: string;
  /** Maîtrise de la classe nécessaire : secondes actives et éliminations de tanks avec cette classe. */
  unlock: { seconds: number; kills: number };
}

/** Seuils proposés (modifiables après essais) : du plus simple au plus exigeant. */
export const STYLE_DEFS: Record<StyleId, StyleDef> = {
  atelier: { name: 'Atelier', material: 'acier brossé, panneaux rivetés', unlock: { seconds: 0, kills: 0 } },
  desert: { name: 'Désert', material: 'sable, camouflage en taches, toile aux embouchures, sacs de sable', unlock: { seconds: 300, kills: 0 } },
  boreal: { name: 'Boréal', material: 'acier bleui, givre et cristaux, bandes isolantes, chenilles cloutées', unlock: { seconds: 0, kills: 10 } },
  obsidienne: { name: 'Obsidienne', material: 'plaques noires facettées, jointures lumineuses', unlock: { seconds: 1200, kills: 0 } },
  signal: { name: 'Signal', material: 'orange haute visibilité, chevrons de danger, gyrophare', unlock: { seconds: 2700, kills: 40 } },
};

/** Détail de caisse propre à chaque famille (dessiné sur la caisse, jamais sur les tubes). */
export const FAMILY_DETAIL: Record<string, string> = {
  Base: 'aucun',
  Assaut: 'caisse de munitions',
  Précision: 'lunette et télémètre',
  Artillerie: 'stabilisateurs',
  Missiles: 'nacelles latérales',
  Lasers: 'bobines de focalisation',
  Drones: 'mât d’antenne',
  Mines: 'plaques de danger',
  Pièges: 'crochets',
  Collision: 'renforts d’angle',
  Dispersion: 'évents',
  Rebond: 'déflecteurs',
  Tourelles: 'capteur arrière',
  Énergie: 'cellules',
  Contrôle: 'parabole',
  Hybrides: 'modules mixtes',
};

/** Galons de rang : niveau 10 → 1, 25–35 → 2, 50 → 3, 60 → 4, 100 → 5, 175 → 6 (petit tank : 0). */
export function rankPips(cls: Pick<ClassNode, 'level' | 'id'>): number {
  if (cls.id === 'BASE') return 0;
  return cls.level >= 175 ? 6 : cls.level >= 100 ? 5 : cls.level >= 60 ? 4 : cls.level >= 50 ? 3 : cls.level >= 25 ? 2 : 1;
}

export interface Mastery {
  seconds: number;
  kills: number;
}

/** Variante disponible : Atelier dès la découverte de la classe, les autres selon la maîtrise. */
export function styleUnlocked(style: StyleId, m: Mastery | undefined): boolean {
  if (style === 'atelier') return true;
  const need = STYLE_DEFS[style].unlock;
  return !!m && m.seconds >= need.seconds && m.kills >= need.kills;
}

export interface VariantEntry {
  id: string;
  classId: string;
  className: string;
  family: string;
  level: number;
  style: StyleId;
  styleName: string;
  material: string;
  familyDetail: string;
  rankPips: number;
  unlock: string;
}

function unlockText(s: StyleId): string {
  const u = STYLE_DEFS[s].unlock;
  if (s === 'atelier') return 'découverte de la classe';
  const parts: string[] = [];
  if (u.seconds) parts.push(`${u.seconds / 60} min actives`);
  if (u.kills) parts.push(`${u.kills} éliminations de tanks`);
  return `${parts.join(' + ')} avec la classe`;
}

/** Manifeste : une entrée par classe jouable et par style (le petit tank de départ n'est pas compté). */
export const VARIANTS: VariantEntry[] = CLASSES.filter((c) => c.id !== 'BASE').flatMap((c) =>
  STYLES.map((s) => ({
    id: `${c.id}:${s}`,
    classId: c.id,
    className: c.name,
    family: c.family,
    level: c.level,
    style: s,
    styleName: STYLE_DEFS[s].name,
    material: STYLE_DEFS[s].material,
    familyDetail: FAMILY_DETAIL[c.family] ?? 'aucun',
    rankPips: rankPips(c),
    unlock: unlockText(s),
  })),
);
